import type { TFunction } from 'i18next';

import { api } from '@/lib/api';
import { downloadCombinedReportPdf } from '@/utils/report-pdf';
import { formatDateTime } from '@/utils/format';

import type { CombinedReportRef } from './download-combined-report-csv';
import { getCustomReportSource } from './custom-report-sources';
import { formatCustomReportCell } from './format-custom-report-cell';
import {
  formatReportExportCell,
  visibleExportColumns,
  type ReportExportColumn,
} from './format-report-export';

interface CombinedPdfSection {
  title: string;
  nameKey?: string;
  kind: 'seeded' | 'custom';
  reportKey?: string;
  objectSource?: string;
  columns: ReportExportColumn[];
  rows: Record<string, unknown>[];
}

interface CombinedPdfResponse {
  rowCount: number;
  sections: CombinedPdfSection[];
}

const formatSectionForPdf = (
  section: CombinedPdfSection,
  t: TFunction<'reports'>,
  locale: string,
): { title: string; columns: ReportExportColumn[]; columnLabels: string[]; rows: string[][] } => {
  const columns =
    section.kind === 'seeded'
      ? visibleExportColumns(section.columns, section.rows)
      : section.columns;

  const columnLabels =
    section.kind === 'seeded'
      ? columns.map((column) => t(`column.${column.key}`, column.key))
      : columns.map((key) => t(`custom.fields.${key.key}`, key.key));

  const rows = section.rows.map((row) =>
    columns.map((column) => {
      if (section.kind === 'custom') {
        const source = section.objectSource ? getCustomReportSource(section.objectSource) : undefined;
        const field = source?.fields.find((item) => item.key === column.key);
        return formatCustomReportCell(field, row[column.key], row, t, locale);
      }
      return formatReportExportCell(column, row[column.key], row, t, { locale });
    }),
  );

  const title =
    section.kind === 'seeded' && section.nameKey
      ? t(section.nameKey, section.title)
      : section.title;

  return { title, columns, columnLabels, rows };
};

export const downloadCombinedReportPdfExport = async (input: {
  churchId: string;
  reports: CombinedReportRef[];
  dateFrom?: string;
  dateTo?: string;
  churchName: string;
  branchName?: string | null;
  logoUrl?: string | null;
  locale: string;
  timezone: string;
  periodLabel?: string;
  t: TFunction<'reports'>;
  tCommon: TFunction;
}): Promise<void> => {
  const data = await api.post<CombinedPdfResponse>('/reports/combined/export', {
    format: 'pdf',
    reports: input.reports,
    dateFrom: input.dateFrom,
    dateTo: input.dateTo,
  });

  const tableSections = data.sections.map((section) => formatSectionForPdf(section, input.t, input.locale));

  await downloadCombinedReportPdf({
    churchName: input.churchName,
    footerLabel: input.tCommon('app.fullName'),
    branchName: input.branchName,
    logoUrl: input.logoUrl,
    title: input.t('combined.pdfTitle'),
    description: input.t('combined.pdfDescription'),
    periodLabel: input.periodLabel,
    generatedLabel: input.t('run.pdfGenerated', {
      when: formatDateTime(new Date().toISOString(), {
        locale: input.locale,
        timezone: input.timezone,
        withZone: true,
      }),
    }),
    tableSections,
    filename: 'combined-report.pdf',
  });
};
